import { formatRelativeTime } from '../lib/relative-time';
import type { PublicNote } from '../api/notes';

interface NotesListProps {
  notes: PublicNote[];
  loading: boolean;
  error: string | null;
}

export function NotesList({ notes, loading, error }: NotesListProps) {
  if (loading) {
    return (
      <p data-testid="notes-loading" style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
        Loading notes…
      </p>
    );
  }
  if (error !== null) {
    return (
      <p data-testid="notes-error" style={{ color: 'var(--error)', fontSize: '0.875rem' }}>
        Couldn’t load notes: {error}
      </p>
    );
  }
  if (notes.length === 0) {
    return (
      <p data-testid="notes-empty" style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
        Be the first to add a note about this landmark.
      </p>
    );
  }

  return (
    <ul
      data-testid="notes-list"
      style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
      }}
    >
      {notes.map((note) => (
        <li
          key={note.id}
          data-testid={`note-${note.id}`}
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-3)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 'var(--space-1)',
              gap: 'var(--space-2)',
            }}
          >
            <strong style={{ fontFamily: 'var(--font-serif)', color: 'var(--text-headline)' }}>
              {note.nickname}
            </strong>
            <time
              dateTime={new Date(note.createdAt * 1000).toISOString()}
              style={{ color: 'var(--text-muted)', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
            >
              {formatRelativeTime(note.createdAt)}
            </time>
          </div>
          <p style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{note.body}</p>
        </li>
      ))}
    </ul>
  );
}
